import { useQuery } from '@tanstack/react-query';
import { coco, ResourceType } from '@cotera/api';
import type { ToolPart } from '@cotera/sdk/core';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';

const { workflowIdFromToolKey, isWorkflowToolKey } = coco;

function normalizeWorkflowToolKey(toolKey: string): string {
  return toolKey.replaceAll('_', '/');
}

function workflowIdForToolPart(part: ToolPart): string | null {
  const workflowPart = part.isWorkflowToolCall() ? part : null;
  if (workflowPart !== null && workflowPart.targetWorkflowId !== null) {
    return workflowPart.targetWorkflowId;
  }

  const normalizedKey = normalizeWorkflowToolKey(part.toolKey().toString());
  return isWorkflowToolKey(normalizedKey)
    ? workflowIdFromToolKey(normalizedKey)
    : null;
}

/**
 * Resolve the human-readable workflow name for a `coco/workflow-*` tool call.
 *
 * Prefer the name embedded in the tool payload; otherwise load the workflow
 * resource by id (from `targetWorkflowId` / the tool key — the same ids used
 * with `targetRunId` for live run status). The run endpoint itself does not
 * carry the workflow name.
 */
export function useWorkflowToolDisplayName(part: ToolPart): string {
  const workflowPart = part.isWorkflowToolCall() ? part : null;
  const payloadName = workflowPart?.workflowName ?? null;
  const workflowId = workflowIdForToolPart(part);

  const client = useTenantedClient();
  const orgId = useWhoami((s) => s.org.id);

  // `queryKeys.resource.get` caches the raw `resource.get` Result — every other
  // reader of this key (the artifact access gate, the workflow/agent/document
  // pages) unwraps it themselves. Cache that shape and project the name with
  // `select`; caching the name alone would hand those readers a bare string.
  const { data: fetchedName } = useQuery({
    queryKey: queryKeys.resource.get({
      orgId,
      type: ResourceType.Workflow,
      id: workflowId ?? '',
    }),
    enabled: payloadName === null && workflowId !== null,
    queryFn: () =>
      client.resource.get({
        type: ResourceType.Workflow,
        id: workflowId ?? '',
      }),
    select: (res): string | null =>
      res.isOk() && res.value.type === 'workflow'
        ? res.value.workflow.name
        : null,
  });

  if (payloadName !== null) {
    return payloadName;
  }

  const name = fetchedName?.trim();
  return name !== undefined && name !== '' ? name : 'Workflow';
}
